/**
 * Tamil Encoding Converter: Unicode to Legacy Encodings (TAM, TAB, TSCII).
 * Refined version using official 8-bit mappings (Extended ASCII 128-255)
 * to resolve character collisions and improve legacy software compatibility.
 */

// Official TAM (Tamil Monolingual) 8-bit glyph mapping
// Based on 1999 Tamil Nadu Govt standard and UTN #18 guidelines.
// Note: These characters will look like symbols (ÌÍÎ) on the web.
const TAM_MAP = {
    // Vowels (Uyir)
    "\u0B85": "\xA1", // அ
    "\u0B86": "\xA2", // ஆ
    "\u0B87": "\xA3", // இ
    "\u0B88": "\xA4", // ஈ
    "\u0B89": "\xA5", // உ
    "\u0B8A": "\xA6", // ஊ
    "\u0B8E": "\xA7", // எ
    "\u0B8F": "\xA8", // ஏ
    "\u0B90": "\xA9", // ஐ
    "\u0B92": "\xAA", // ஒ
    "\u0B93": "\xAB", // ஓ
    "\u0B94": "\xAC", // ஔ
    "\u0B83": "\xAD", // ஃ

    // Consonants (Mei)
    "\u0B95": "\xB1", // க
    "\u0B99": "\xB2", // ங
    "\u0B9A": "\xB3", // ச
    "\u0B9E": "\xB4", // ஞ
    "\u0B9F": "\xB5", // ட
    "\u0BA3": "\xB6", // ண
    "\u0BA4": "\xB7", // த
    "\u0BA8": "\xB8", // ந
    "\u0BA9": "\xC2", // ன (at end for proper ordering)
    "\u0BAA": "\xB9", // ப
    "\u0BAE": "\xBA", // ம
    "\u0BAF": "\xBB", // ய
    "\u0BB0": "\xBC", // ர
    "\u0BB1": "\xC1", // ற
    "\u0BB2": "\xBD", // ல
    "\u0BB3": "\xC0", // ள
    "\u0BB4": "\xBF", // ழ
    "\u0BB5": "\xBE", // வ

    // Grantha
    "\u0B9C": "\xD1", // ஜ
    "\u0BB7": "\xD2", // ஷ
    "\u0BB8": "\xD3", // ஸ
    "\u0BB9": "\xD4", // ஹ
    "\u0B95\u0BCD\u0BB7": "\xD5", // க்ஷ

    // Vowel Signs (Uyirmei)
    "\u0BBE": "\xFE", // ா
    "\u0BBF": "\xFD", // ி
    "\u0BB0\u0BBF": "\xEC", // ரி (Specific ligature example)
    "\u0BC0": "\xFC", // ீ
    "\u0BC1": "\xFB", // ு
    "\u0BC2": "\xFA", // ூ
    "\u0BC6": "\xF9", // ெ (Pre)
    "\u0BC7": "\xF8", // ே (Pre)
    "\u0BC8": "\xF7", // ை (Pre)
    "\u0BCD": "\xF6", // ் (Pulli)
    "\u0BD7": "\xF5", // ௌ (vowel sign post part)

    // Combined/Sandwich vowel signs (Sandwich logic)
    "\u0BCA": ["\xF9", "\xFE"], // ொ (ெ + ா)
    "\u0BCB": ["\xF8", "\xFE"], // ோ (ே + ா)
    "\u0BCC": ["\xF9", "\xF5"], // ௌ (ெ + s)
};

// TAB (Tamil Bilingual) - simplified for now
const TAB_MAP = {
    ...TAM_MAP,
    // Add variations if needed
};

// TSCII mapping (Simplified subset)
const TSCII_MAP = {
    "\u0B85": "\xAB",
    "\u0B86": "\xAC",
    // ...
};

/**
 * Converts Unicode Tamil string to specified legacy encoding.
 */
export function convertUnicodeToLegacy(text, encoding) {
    if (!text || encoding === "Unicode") return text;

    const map = encoding === "TAM" ? TAM_MAP : (encoding === "TAB" ? TAB_MAP : TSCII_MAP);
    let result = "";
    let i = 0;

    while (i < text.length) {
        let char = text[i];
        let next = text[i + 1] || "";

        // Check for common ligatures or compound characters
        if (char === "\u0B95" && next === "\u0BCD" && text[i + 2] === "\u0BB7") {
            result += map["\u0B95\u0BCD\u0BB7"] || "\xD5";
            i += 3;
            continue;
        }

        // Special handling for vowel signs
        if (next && "\u0BBE\u0BBF\u0BC0\u0BC1\u0BC2\u0BC6\u0BC7\u0BC8\u0BCA\u0BCB\u0BCC\u0BCD".includes(next)) {
            // Handle Pre-vowel signs: ெ, ே, ை
            if ("\u0BC6\u0BC7\u0BC8".includes(next)) {
                result += map[next] || ""; // The Pre part
                result += map[char] || char; // The consonant
                i += 2;
                continue;
            }
            // Handle Sandwich-vowel signs: ொ, ோ, ௌ
            if ("\u0BCA\u0BCB\u0BCC".includes(next)) {
                const parts = map[next];
                if (Array.isArray(parts)) {
                    result += parts[0]; 
                    result += map[char] || char;
                    result += parts[1];
                } else {
                    result += map[char] || char;
                    result += parts || next;
                }
                i += 2;
                continue;
            }
            // Regular post-vowel sign or pulli
            result += map[char] || char;
            result += map[next] || next;
            i += 2;
            continue;
        }

        // Standalone character
        result += map[char] || char;
        i++;
    }

    return result;
}

export const TAMIL_ENCODINGS = ["Unicode", "TAM", "TAB", "TSCII"];

export const TAMIL_FONTS = [
    // Standard Unicode (Works on browse/web)
    "Latha", "Nirmala UI", "Vijaya", "Tau-Marutham",
    // Legacy TAM Encodings (Requires local Windows font installed)
    "TAM-Kalyani", "TAM-Kamban", "TAM-Valluvar", "TAM-Adavi",
    // Legacy TAB/TSCII Encodings
    "TAB-Anna", "TAB-Bharani", "TSC_Paranar",
];
